// Localização: ./src/components/AIAssistant/ConversationHistory.jsx
// Componente para exibir histórico de conversas anteriores

import React, { useState, useEffect } from 'react';
import { getConversations, deleteSession } from '../../api/aiApi';
import { History, Search, X, Trash2, MessageSquare, Plus } from 'lucide-react';

/**
 * Componente de histórico de conversas
 * @param {boolean} isOpen - Se o painel está aberto
 * @param {function} onClose - Função para fechar o painel
 * @param {function} onSelectConversation - Função chamada ao selecionar uma conversa
 * @param {function} onNewConversation - Função chamada ao iniciar nova conversa
 */
const ConversationHistory = ({ isOpen, onClose, onSelectConversation, onNewConversation }) => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingId, setDeletingId] = useState(null);

    // Carregar conversas
    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await getConversations({ limit: 100 });
            if (response.data.success) {
                setConversations(response.data.conversations || []);
            }
        } catch (error) {
            console.error('Erro ao carregar conversas:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, conversation) => {
        e.stopPropagation();
        
        if (!window.confirm(`Tem certeza que deseja excluir esta conversa${conversation.message_count > 1 ? ` (${conversation.message_count} mensagens)` : ''}?`)) {
            return;
        }

        setDeletingId(conversation.session_id);
        try {
            await deleteSession(conversation.session_id);
            setConversations(conversations.filter(c => c.session_id !== conversation.session_id));
        } catch (error) {
            console.error('Erro ao deletar conversa:', error);
            alert('Erro ao deletar conversa. Tente novamente.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleSelect = (conversation) => {
        if (onSelectConversation) {
            onSelectConversation(conversation);
        }
        onClose();
    };

    const handleNewConversation = () => {
        if (onNewConversation) {
            onNewConversation();
        }
        onClose();
    };

    // Filtrar conversas por termo de busca
    const filteredConversations = conversations.filter(conv => {
        const searchLower = searchTerm.toLowerCase();
        return (
            conv.mensagem_usuario?.toLowerCase().includes(searchLower) ||
            conv.mensagem_ia?.toLowerCase().includes(searchLower) ||
            conv.intent?.toLowerCase().includes(searchLower)
        );
    });

    // Agrupar conversas por data
    const groupedConversations = filteredConversations.reduce((groups, conv) => {
        const date = new Date(conv.created_at);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        let dateKey;
        if (date.toDateString() === today.toDateString()) {
            dateKey = 'Hoje';
        } else if (date.toDateString() === yesterday.toDateString()) {
            dateKey = 'Ontem';
        } else {
            dateKey = date.toLocaleDateString('pt-BR', { 
                day: 'numeric', 
                month: 'long',
                year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
            });
        }

        if (!groups[dateKey]) {
            groups[dateKey] = [];
        }
        groups[dateKey].push(conv);
        return groups;
    }, {});

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const truncateText = (text, maxLength = 60) => {
        if (!text) return 'Sem mensagem';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Overlay */}
            <div 
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />
            
            {/* Painel lateral */}
            <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                            <History className="h-5 w-5" />
                            <h2 className="text-lg font-semibold">Histórico de Conversas</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                            aria-label="Fechar"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    {/* Botão Nova Conversa */}
                    <button
                        onClick={handleNewConversation}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium backdrop-blur-sm border border-white/30"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Nova Conversa</span>
                    </button>
                </div>

                {/* Busca */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar conversas..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Lista de conversas */}
                <div className="flex-1 overflow-y-auto h-[calc(100vh-240px)]">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        </div>
                    ) : Object.keys(groupedConversations).length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                            <MessageSquare className="h-12 w-12 mb-4 text-gray-300" />
                            <p className="text-sm">
                                {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
                            </p>
                        </div>
                    ) : (
                        <div className="p-4 space-y-6">
                            {Object.entries(groupedConversations).map(([dateKey, convs]) => (
                                <div key={dateKey} className="space-y-2">
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2">
                                        {dateKey}
                                    </h3>
                                    {convs.map((conversation) => (
                                        <div
                                            key={conversation.id}
                                            onClick={() => handleSelect(conversation)}
                                            className="group relative p-3 bg-gray-50 hover:bg-green-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-green-200"
                                        >
                                            {/* Botão de deletar */}
                                            <button
                                                onClick={(e) => handleDelete(e, conversation)}
                                                disabled={deletingId === conversation.session_id}
                                                className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity rounded"
                                                aria-label="Deletar conversa"
                                            >
                                                {deletingId === conversation.session_id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </button>

                                            {/* Conteúdo */}
                                            <div className="pr-8">
                                                <p className="text-sm font-medium text-gray-900 mb-1">
                                                    {truncateText(conversation.mensagem_usuario)}
                                                </p>
                                                <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                                                    {truncateText(conversation.mensagem_ia, 50)}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-xs text-gray-400">
                                                            {formatTime(conversation.created_at)}
                                                        </span>
                                                        {conversation.message_count > 1 && (
                                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                                {conversation.message_count} mensagens
                                                            </span>
                                                        )}
                                                    </div>
                                                    {conversation.intent && (
                                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                                                            {conversation.intent}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversationHistory;

